// Region-Routing fuer Methoden-Quadranten (V1: MoSCoW).
//
// Verantwortlichkeiten:
//   - Hit-Test: welche Region liegt unter einem Welt-Punkt?
//   - Lookup: welche Region eines Method-Blocks hat semanticHint.priority === X?
//   - Grid-Slot: wo kann ich einen Req der Groesse W×H in die Region legen, ohne andere
//     bereits einsortierte Reqs zu treffen? Null wenn voll.
//   - Auto-Expand: wenn voll, Block + Regions symmetrisch in der Hoehe wachsen lassen
//     (cascadeResizeRegions reuse — die zieht Children mit).
//
// Wichtig: die Funktionen sind pure (lesen yMap, geben Werte zurueck) — Schreib-Operationen
// nur in expandMoscowBlock, dort ueber yMap.doc.transact.

import {
  type BlockObj,
  type CanvasObject,
  type FrameObj,
  type Priority,
  REGION_PADDING,
  type RegionObj,
  isCanvasObject,
} from "@ms-whiteboard/shared";
import type * as Y from "yjs";
import { growRegion, growStrategyForMethod } from "./auto-grow.js";
import { objectBounds } from "./object-bounds.js";

/** Schritt um den der Block beim Auto-Expand wachsen darf (pro Iteration, Hoehe). */
export const MOSCOW_GROW_STEP_PX = 120;
/** Wie oft wir maximal expandieren bevor wir aufgeben und mit Ueberlappung platzieren. */
export const MAX_AUTO_EXPANDS = 5;
/** Gap zwischen Reqs innerhalb einer Region. */
const SLOT_GAP_PX = 8;

/** Erste Region (in Iterationsreihenfolge), die einen Welt-Punkt enthaelt. */
export function findRegionAt(
  yMap: Y.Map<unknown>,
  worldX: number,
  worldY: number,
): RegionObj | null {
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    if (value.type !== "region") continue;
    if (
      worldX >= value.x &&
      worldX <= value.x + value.width &&
      worldY >= value.y &&
      worldY <= value.y + value.height
    ) {
      return value;
    }
  }
  return null;
}

/** Erster Frame (in Iterationsreihenfolge), der einen Welt-Punkt enthaelt. */
export function findFrameAt(
  yMap: Y.Map<unknown>,
  worldX: number,
  worldY: number,
): FrameObj | null {
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    if (value.type !== "frame") continue;
    if (
      worldX >= value.x &&
      worldX <= value.x + value.width &&
      worldY >= value.y &&
      worldY <= value.y + value.height
    ) {
      return value;
    }
  }
  return null;
}

/**
 * Generisches Region-Lookup ueber einen beliebigen semanticHint-Schluessel.
 * Beispiel: findRegionForHintInBlock(yMap, blockId, "severity", "high")
 *
 * Loest die alte priority-spezifische findRegionForPriorityInBlock ab — letztere
 * bleibt als Convenience-Wrapper bestehen (delegiert auf diese Funktion).
 */
export function findRegionForHintInBlock(
  yMap: Y.Map<unknown>,
  blockId: string,
  hintField: string,
  hintValue: string,
  /** Optionaler Typ-Filter: nur Regionen, die diesen Objekt-Typ akzeptieren (accepts).
   *  Verhindert z.B. dass eine Dependency bei Severity-Wechsel in eine Pain-Region springt,
   *  weil beide denselben severity-Hint tragen. */
  acceptType?: string,
): RegionObj | null {
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    if (value.type !== "region") continue;
    if (value.blockId !== blockId) continue;
    if (value.semanticHint?.[hintField] !== hintValue) continue;
    if (acceptType && !(value.accepts === "any" || value.accepts === acceptType)) continue;
    return value;
  }
  return null;
}

/**
 * Generisches Region-Lookup ueber einen Method-Block (per methodId) + Hint-Feld + Wert.
 * Loest die alte priority-spezifische findRegionForPriority ab — letztere bleibt
 * als Convenience-Wrapper bestehen.
 */
export function findRegionForHint(
  yMap: Y.Map<unknown>,
  methodId: string,
  hintField: string,
  hintValue: string,
): RegionObj | null {
  // Erst den Block mit der methodId finden — es kann mehrere geben (z.B. zwei MoSCoW-Boards),
  // wir nehmen den ersten. Caller, die einen spezifischen Block meinen, sollten
  // findRegionForHintInBlock direkt nutzen.
  let blockId: string | null = null;
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    if (value.type === "block" && value.methodId === methodId) {
      blockId = value.id;
      break;
    }
  }
  if (!blockId) return null;
  return findRegionForHintInBlock(yMap, blockId, hintField, hintValue);
}

/**
 * Sucht die Region eines bestimmten Method-Blocks deren semanticHint.priority === priority.
 * Wird genutzt fuer Initial-Pass + Priority-Change-Routing.
 *
 * Backwards-Compat-Wrapper um findRegionForHint("priority", ...).
 */
export function findRegionForPriority(
  yMap: Y.Map<unknown>,
  methodId: string,
  priority: Priority,
): RegionObj | null {
  return findRegionForHint(yMap, methodId, "priority", priority);
}

/**
 * Wie findRegionForPriority, aber fuer einen explizit angegebenen Block.
 *
 * Backwards-Compat-Wrapper um findRegionForHintInBlock("priority", ...).
 */
export function findRegionForPriorityInBlock(
  yMap: Y.Map<unknown>,
  blockId: string,
  priority: Priority,
): RegionObj | null {
  return findRegionForHintInBlock(yMap, blockId, "priority", priority);
}

/** Liefert den Block-Snapshot zu einer Region (via blockId-FK). */
export function findBlockOfRegion(yMap: Y.Map<unknown>, region: RegionObj): BlockObj | null {
  const raw = yMap.get(region.blockId);
  if (!isCanvasObject(raw)) return null;
  if (raw.type !== "block") return null;
  return raw;
}

/**
 * Sammelt alle Objekte, die direkt in einer Region verankert sind (parentRegionId === region.id).
 * Region- und Block-Children selbst werden ausgeschlossen.
 */
export function objectsInRegion(yMap: Y.Map<unknown>, regionId: string): CanvasObject[] {
  const result: CanvasObject[] = [];
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    if (value.parentRegionId !== regionId) continue;
    if (value.type === "region" || value.type === "block") continue;
    result.push(value);
  }
  return result;
}

/**
 * Findet einen freien Grid-Slot in einer Region fuer einen Req der Groesse candidateSize.
 *
 * Layout:
 *   - Innenraum = region minus REGION_PADDING (header oben, outer sonst)
 *   - Slots werden zeilenweise von oben-links durchgespielt
 *   - Pro Slot: AABB-Check gegen alle bereits in der Region einsortierten Objekte
 *   - Erste kollisionsfreie Position wird zurueckgegeben (Welt-Koordinaten)
 *   - excludeId: ein Objekt das beim Kollisionscheck ignoriert wird (z.B. das gerade
 *     bewegte Req — sonst kollidiert es mit sich selbst)
 *
 * Null wenn kein Slot frei. Caller entscheidet: Auto-Expand oder ueberlappen.
 */
export function findFreeSlotInRegion(
  yMap: Y.Map<unknown>,
  region: RegionObj,
  candidate: { width: number; height: number },
  excludeId?: string,
): { x: number; y: number } | null {
  const innerX = region.x + REGION_PADDING.outer;
  const innerY = region.y + REGION_PADDING.header;
  const innerW = region.width - 2 * REGION_PADDING.outer;
  const innerH = region.height - REGION_PADDING.header - REGION_PADDING.outer;
  if (innerW < candidate.width || innerH < candidate.height) return null;

  const stepX = candidate.width + SLOT_GAP_PX;
  const stepY = candidate.height + SLOT_GAP_PX;
  const cols = Math.max(1, Math.floor((innerW + SLOT_GAP_PX) / stepX));
  const rows = Math.max(1, Math.floor((innerH + SLOT_GAP_PX) / stepY));

  const existing = objectsInRegion(yMap, region.id).filter((o) => o.id !== excludeId);

  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const x = innerX + c * stepX;
      const y = innerY + r * stepY;
      const candRect = { x, y, w: candidate.width, h: candidate.height };
      let collision = false;
      for (const o of existing) {
        const b = objectBounds(o);
        if (rectIntersect(candRect, { x: o.x, y: o.y, w: b.w, h: b.h })) {
          collision = true;
          break;
        }
      }
      if (!collision) return { x, y };
    }
  }
  return null;
}

/**
 * Block-Auto-Expand: vergroessert die Hoehe des Blocks und skaliert alle Regions
 * proportional (Cascade-Resize zieht Children mit).
 *
 * Muss innerhalb einer yMap.doc.transact aufgerufen werden — sonst verteilt sich
 * der Resize ueber mehrere Yjs-Updates und Children sehen einen halb-resized Zustand.
 *
 * Wrapper auf die generische growRegion-Funktion mit "proportional"-Strategie.
 * Bleibt aus Backwards-Compat-Gruenden erhalten — neue Aufrufer sollten growRegion direkt nutzen.
 */
export function expandMoscowBlock(
  yMap: Y.Map<unknown>,
  blockId: string,
  deltaHeightPx: number,
): void {
  const blockRaw = yMap.get(blockId);
  if (!isCanvasObject(blockRaw) || blockRaw.type !== "block") return;
  // Wir brauchen ein Region-Objekt fuer growRegion's API — irgendeine Region des Blocks reicht.
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    if (value.type !== "region") continue;
    if (value.blockId !== blockId) continue;
    growRegion(yMap, value, "proportional", deltaHeightPx);
    return;
  }
}

/**
 * Convenience: findet freien Slot, expandiert ggf. iterativ den Block, bis Platz frei wird.
 * Gibt am Ende eine Position zurueck (auch wenn nach MAX_AUTO_EXPANDS noch nichts frei ist
 * — dann mit potentieller Ueberlappung an Slot 0,0). Caller muss innerhalb yMap.doc.transact sein.
 */
export function findFreeSlotWithAutoExpand(
  yMap: Y.Map<unknown>,
  region: RegionObj,
  candidate: { width: number; height: number },
  excludeId?: string,
): { x: number; y: number } {
  let currentRegion = region;
  let slot = findFreeSlotInRegion(yMap, currentRegion, candidate, excludeId);
  if (slot) return slot;

  const block = findBlockOfRegion(yMap, currentRegion);
  if (!block) {
    // kein Block — kann nicht expandieren, gib gridded slot trotz Voll-Region zurueck
    return {
      x: currentRegion.x + REGION_PADDING.outer,
      y: currentRegion.y + REGION_PADDING.header,
    };
  }

  // Auto-Grow-Strategie aus der Methode des Blocks ableiten (Spec §54-59) — statt
  // hartkodiert proportional. lane-local/region-local/block-height greifen damit.
  const strategy = growStrategyForMethod(block.methodId);
  for (let i = 0; i < MAX_AUTO_EXPANDS; i += 1) {
    growRegion(yMap, currentRegion, strategy, MOSCOW_GROW_STEP_PX);
    // Region neu lesen — Grow hat ihre Geometrie veraendert.
    const refreshed = yMap.get(currentRegion.id);
    if (isCanvasObject(refreshed) && refreshed.type === "region") {
      currentRegion = refreshed;
    }
    slot = findFreeSlotInRegion(yMap, currentRegion, candidate, excludeId);
    if (slot) return slot;
  }

  // Notfall: gib top-left zurueck, akzeptiere Ueberlappung
  return {
    x: currentRegion.x + REGION_PADDING.outer,
    y: currentRegion.y + REGION_PADDING.header,
  };
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
function rectIntersect(a: Rect, b: Rect): boolean {
  return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
}

/**
 * Generischer High-Level-Hint-Router: nimmt ein Objekt + eine Region + den
 * semanticHint-Feldnamen und liefert Slot-Position + den extrahierten Hint-Wert.
 *
 * Die Schreib-Operation muss der Caller selbst durchfuehren (innerhalb von
 * yMap.doc.transact). Caller patcht das Objekt typischerweise mit
 * { x, y, parentRegionId: region.id, [hintField]: hintValue }.
 *
 * Beispiel:
 *   const result = routeByHint(yMap, obj, region, "severity");
 *   if (result) yMap.set(obj.id, { ...obj, x: result.x, y: result.y, severity: result.hintValue as Severity, parentRegionId: region.id });
 *
 * Gibt `null` zurueck, wenn die Region keinen Wert fuer das gewuenschte Hint-Feld traegt.
 */
export interface HintRouteResult {
  x: number;
  y: number;
  /** Wert aus region.semanticHint[hintField]. Caller castet ihn passend (z.B. Priority). */
  hintValue: string;
}

export function routeByHint(
  yMap: Y.Map<unknown>,
  obj: CanvasObject,
  region: RegionObj,
  hintField: string,
): HintRouteResult | null {
  const hintValue = region.semanticHint?.[hintField];
  if (!hintValue) return null;
  const bounds = objectBounds(obj);
  const slot = findFreeSlotWithAutoExpand(
    yMap,
    region,
    { width: bounds.w, height: bounds.h },
    obj.id,
  );
  return { x: slot.x, y: slot.y, hintValue };
}
